import React from 'react';
import { Star, CheckCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const TemplateRecommendation = ({ answers, onSelectTemplate }) => {
  // Template recommendation logic based on answers
  const getRecommendedTemplates = (answers) => {
    const { experience_level, job_type, education_level, location_preference } = answers;
    
    // Define template categories and their characteristics
    const templateCategories = {
      modern: {
        name: 'Modern',
        description: 'Clean, contemporary design with bold typography',
        best_for: ['tech', 'creative', 'business'],
        experience: ['entry', 'mid', 'senior'],
        education: ['bachelor', 'master', 'phd']
      },
      professional: {
        name: 'Professional',
        description: 'Traditional, conservative design for corporate environments',
        best_for: ['business', 'finance', 'healthcare', 'education'],
        experience: ['mid', 'senior', 'executive'],
        education: ['bachelor', 'master', 'phd', 'certification']
      },
      creative: {
        name: 'Creative',
        description: 'Unique, artistic design for creative professionals',
        best_for: ['creative', 'tech'],
        experience: ['entry', 'mid', 'senior'],
        education: ['bachelor', 'master', 'associate']
      },
      executive: {
        name: 'Executive',
        description: 'Sophisticated design for leadership positions',
        best_for: ['business', 'finance', 'tech'],
        experience: ['senior', 'executive'],
        education: ['master', 'phd', 'bachelor']
      },
      minimalist: {
        name: 'Minimalist',
        description: 'Simple, clean design focusing on content',
        best_for: ['tech', 'creative', 'business'],
        experience: ['entry', 'mid', 'senior'],
        education: ['bachelor', 'master', 'phd', 'associate']
      }
    };

    // Score each category based on user answers
    const scoredCategories = Object.entries(templateCategories).map(([key, category]) => {
      let score = 0;
      
      // Job type match
      if (category.best_for.includes(job_type)) score += 3;
      
      // Experience level match
      if (category.experience.includes(experience_level)) score += 2;
      
      // Education level match
      if (category.education.includes(education_level)) score += 1;
      
      return { key, ...category, score };
    });

    // Sort by score and return top 3
    return scoredCategories
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  };

  const recommendedTemplates = getRecommendedTemplates(answers);

  // Generate specific template recommendations
  const generateTemplateRecommendations = () => {
    const templates = [
      {
        id: 'modern-1',
        name: 'Modern Professional',
        category: 'modern',
        description: 'Clean lines with bold headers and modern typography',
        preview: '/templates/modern-1-preview.png',
        features: ['ATS-friendly', 'Modern design', 'Easy to customize'],
        best_for: 'Technology and creative roles'
      },
      {
        id: 'professional-1',
        name: 'Classic Corporate',
        category: 'professional',
        description: 'Traditional layout perfect for corporate environments',
        preview: '/templates/professional-1-preview.png',
        features: ['Conservative design', 'ATS-optimized', 'Professional look'],
        best_for: 'Business and finance roles'
      },
      {
        id: 'creative-1',
        name: 'Creative Portfolio',
        category: 'creative',
        description: 'Unique design that showcases creativity and personality',
        preview: '/templates/creative-1-preview.png',
        features: ['Eye-catching design', 'Portfolio integration', 'Creative layout'],
        best_for: 'Design and creative roles'
      },
      {
        id: 'executive-1',
        name: 'Executive Leadership',
        category: 'executive',
        description: 'Sophisticated design for senior leadership positions',
        preview: '/templates/executive-1-preview.png',
        features: ['Executive presence', 'Leadership focus', 'Premium design'],
        best_for: 'Senior management roles'
      },
      {
        id: 'minimalist-1',
        name: 'Clean Minimalist',
        category: 'minimalist',
        description: 'Simple, elegant design that lets your content shine',
        preview: '/templates/minimalist-1-preview.png',
        features: ['Clean design', 'Easy to read', 'Content-focused'],
        best_for: 'All industries'
      }
    ];

    // Filter templates based on recommendations
    const recommendedTemplateIds = recommendedTemplates.map(t => t.key);
    return templates.filter(template => 
      recommendedTemplateIds.includes(template.category)
    );
  };

  const templateRecommendations = generateTemplateRecommendations();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Perfect Templates for You
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Based on your answers, we've selected the best resume templates that match your experience level, industry, and career goals.
          </p>
        </div>

        {/* Recommendation Summary */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <Star className="w-6 h-6 text-yellow-500 mr-2" />
            Your Recommended Style
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendedTemplates.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border-2 ${
                  index === 0 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                  {index === 0 && (
                    <span className="text-xs bg-primary-600 text-white px-2 py-1 rounded-full">
                      Best Match
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{category.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {templateRecommendations.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              {/* Template Preview */}
              <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-20 bg-white rounded border-2 border-gray-300 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Template Preview</p>
                </div>
              </div>

              {/* Template Info */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {template.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {template.description}
                </p>

                {/* Features */}
                <div className="space-y-2 mb-4">
                  {template.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      {feature}
                    </div>
                  ))}
                </div>

                <div className="text-xs text-gray-500 mb-4">
                  Best for: {template.best_for}
                </div>

                <button
                  onClick={() => onSelectTemplate(template)}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center"
                >
                  Use This Template
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Templates Button */}
        <div className="text-center">
          <button
            onClick={() => onSelectTemplate(null)} // null means show all templates
            className="inline-flex items-center px-6 py-3 border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200"
          >
            View All 20 Templates
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateRecommendation;
